/**
 * Payer Enrollments Tab
 *
 * Payer-centric enrollment management: search a payer and enroll for every
 * transaction type it requires in one action, then see one card per payer with
 * per-transaction status and provider-task completion.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Enrollment } from '@features/enrollments';
import { useEnrollmentOperations, useEnrollments } from '@features/enrollments';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  OpenInNew as OpenInNewIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { PayerResponse } from '@store/generated/payers';
import { useSnackbar } from 'notistack';

import { useEnrollAllRequiredTransactions } from '../hooks/useEnrollAllRequiredTransactions';
import {
  getEnrollableTransactionTypes,
  getTransactionTypeLabel,
  isPendingStatus,
} from '../utils/enrollmentStatus';
import { extractApiErrorMessage, handleEnrollmentError } from '../utils/errorUtils';

import { PayerEnrollmentGroup } from './PayerEnrollmentGroup';
import { PayerSearchDropdown } from './PayerSearchDropdown';

interface PayerEnrollmentsTabProps {
  embedded?: boolean;
  /** Default billing entity ID for enrollments */
  defaultEntityId: string | null;
  /** Called when user needs to open the setup wizard */
  onOpenWizard: (initialStep?: number) => void;
}

export function PayerEnrollmentsTab({
  defaultEntityId,
  onOpenWizard,
  embedded = false,
}: PayerEnrollmentsTabProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // Enrollment data and operations
  const {
    enrollments,
    isLoading: enrollmentsLoading,
    refetch: refetchEnrollments,
  } = useEnrollments();
  const { initiate, initiateLoading, syncAll, syncAllLoading } = useEnrollmentOperations();

  // Local state
  const { enrollAllRequiredTransactions, enrollingPayerId } = useEnrollAllRequiredTransactions();
  const hasAutoSynced = useRef(false);

  // Auto-sync on mount. An empty list is not a reason to skip: enrollments
  // created directly in the Stedi portal only appear after a sync discovers
  // and imports them, so empty is exactly when syncing matters most.
  useEffect(() => {
    if (!hasAutoSynced.current && !syncAllLoading && !enrollmentsLoading) {
      const needsSync =
        enrollments.length === 0 || enrollments.some(e => isPendingStatus(e.status));
      if (needsSync) {
        hasAutoSynced.current = true;
        syncAll()
          .unwrap()
          .then(() => {
            refetchEnrollments();
          })
          .catch(() => {
            // Silent fail for auto-sync
          });
      }
    }
  }, [enrollments, syncAllLoading, enrollmentsLoading, syncAll, refetchEnrollments]);

  // Enroll a payer for every transaction type it requires.
  const handleEnroll = useCallback(
    async (payer: PayerResponse) => {
      if (!defaultEntityId) {
        enqueueSnackbar('Please set up a billing identity first', { variant: 'warning' });
        onOpenWizard(1);
        return;
      }
      const payerId = payer.id;
      if (!payerId) return;

      if (getEnrollableTransactionTypes(payer).length === 0) {
        enqueueSnackbar(`${payer.display_name} doesn't require enrollment.`, { variant: 'info' });
        return;
      }

      const { succeeded, failed, errors } = await enrollAllRequiredTransactions(
        payer,
        transactionType =>
          initiate({
            initiateEnrollmentRequest: {
              billing_entity_id: defaultEntityId,
              payer_uuid: payerId,
              transaction_type: transactionType,
            },
          }).unwrap(),
      );

      if (failed.length === 0) {
        enqueueSnackbar(
          `Enrollment requested for ${payer.display_name} (${succeeded
            .map(getTransactionTypeLabel)
            .join(', ')}). Review usually takes 1-2 weeks.`,
          { variant: 'success' },
        );
      } else if (succeeded.length > 0) {
        enqueueSnackbar(
          `Enrolled ${payer.display_name} for ${succeeded
            .map(getTransactionTypeLabel)
            .join(', ')}, but failed for ${failed
            .map(getTransactionTypeLabel)
            .join(', ')}: ${extractApiErrorMessage(errors[errors.length - 1])}`,
          { variant: 'warning', autoHideDuration: 8000 },
        );
      } else {
        handleEnrollmentError(errors[errors.length - 1], enqueueSnackbar, onOpenWizard);
      }

      refetchEnrollments();
    },
    [
      defaultEntityId,
      initiate,
      enqueueSnackbar,
      onOpenWizard,
      refetchEnrollments,
      enrollAllRequiredTransactions,
    ],
  );

  // Handle sync all
  const handleSyncAll = useCallback(async () => {
    try {
      await syncAll().unwrap();
      enqueueSnackbar('All enrollments synced successfully', { variant: 'success' });
      refetchEnrollments();
    } catch (err) {
      enqueueSnackbar('Failed to sync enrollments. Please try again.', { variant: 'error' });
    }
  }, [syncAll, enqueueSnackbar, refetchEnrollments]);

  // Group enrollments by payer so each payer is one card with per-transaction rows.
  const payerGroups = useMemo(() => {
    const groups = new Map<string, Enrollment[]>();
    for (const enrollment of enrollments) {
      const key =
        enrollment.stedi_payer_id ||
        enrollment.payer_uuid ||
        enrollment.stedi_transaction_enrollment_id;
      const existing = groups.get(key);
      if (existing) {
        existing.push(enrollment);
      } else {
        groups.set(key, [enrollment]);
      }
    }
    return [...groups.entries()];
  }, [enrollments]);

  // Show setup prompt if no billing entity
  if (!defaultEntityId) {
    return (
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          ...(embedded && {
            border: 0,
            borderRadius: 0,
            boxShadow: 'none',
            '& > .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } },
          }),
        }}
      >
        <CardContent>
          <Alert severity="info">
            <Typography variant="subtitle2" gutterBottom>
              Complete identity setup first
            </Typography>
            <Typography variant="body2">
              You need to add and verify at least one billing identity before managing payer
              enrollments.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => onOpenWizard(1)}
              sx={{ mt: 2 }}
            >
              Add Billing Identity
            </Button>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        ...(embedded && {
          border: 0,
          borderRadius: 0,
          boxShadow: 'none',
          '& > .MuiCardContent-root': { p: 0, '&:last-child': { pb: 0 } },
        }),
      }}
    >
      <CardContent>
        {/* Header with Actions */}
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: embedded ? 'flex-end' : 'space-between',
            alignItems: 'flex-start',
          }}
        >
          {!embedded && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                Payer Enrollments
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Some insurance companies require a one-time enrollment before accepting electronic
                claims or sending remittances. Search for a payer to check what&apos;s required and
                enroll.
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="View enrollments in Stedi dashboard" arrow>
              <IconButton
                size="small"
                onClick={() =>
                  window.open('https://portal.stedi.com/app/healthcare/enrollments', '_blank')
                }
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {enrollments.length > 0 && (
              <Tooltip title="Sync all enrollment statuses" arrow>
                <span>
                  <IconButton size="small" onClick={handleSyncAll} disabled={syncAllLoading}>
                    {syncAllLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <SyncIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Payer Search */}
        <Box sx={{ mb: 3 }}>
          <PayerSearchDropdown
            onEnroll={handleEnroll}
            isEnrolling={initiateLoading}
            enrollingPayerId={enrollingPayerId}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* My Enrollments Section */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            My Enrollments {payerGroups.length > 0 && `(${payerGroups.length})`}
          </Typography>

          {enrollmentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : payerGroups.length === 0 ? (
            <EmptyEnrollmentsState />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {payerGroups.map(([key, group]) => (
                <PayerEnrollmentGroup key={key} enrollments={group} />
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function EmptyEnrollmentsState() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        py: 4,
        px: 3,
        textAlign: 'center',
        border: `1px dashed ${theme.palette.grey[300]}`,
        borderRadius: 2,

        bgcolor: theme.palette.grey[50],
      }}
    >
      <AssignmentIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 1 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        No Enrollments Yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Most payers allow claims without enrollment. Search for a payer above to check if enrollment
        is required.
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Tip: Enrollment is typically only needed for receiving Electronic Remittance Advice
        (ERA/835).
      </Typography>
    </Box>
  );
}
